import React, { useMemo } from 'react';
import { Box, Button, ButtonBase, Paper, Stack, Typography } from '@mui/material';
import { ArrowForward, CheckCircleRounded, MapOutlined, Refresh } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../store';
import { TONES, Tone } from '../../../components/ui/PageKit';
import type { Machine, MaterialOrigin, Shift, StockStatusItem } from '../../../types/factory';
import { addDaysIso, fmtPct, fmtQty } from '../shared';
import { plantLayoutForTenant, PlantZone } from './layouts';
import {
  AR_TZ, PlantAlert, PlantData, fmtDayShort, fmtKg, fmtWhen, findMachine, isMold, molds, plantAlerts, shiftsToday, usePlantData,
  zoneStatus, ZoneStatus,
} from './data';
import { ACCENT, ACCENT_SOFT, AnimatedNumber, INK, LINE, MUTED, ProgressBar, PulseDot, SURFACE, accentButton } from './ui';

// Inicio del sistema de planta: qué pasa hoy en la fábrica de un vistazo. Todo sale del mismo juego de datos
// que el plano (se refresca solo cada minuto) y cada bloque lleva a la pantalla donde se resuelve.

const EASE = [0.16, 1, 0.3, 1] as const;

const Reveal: React.FC<{ i?: number; children: React.ReactNode; style?: React.CSSProperties }> = ({ i = 0, children, style }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 + i * 0.06, ease: EASE }} style={style}>
    {children}
  </motion.div>
);

const Card: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode; action?: { label: string; onClick: () => void }; children: React.ReactNode }> = ({
  title, subtitle, action, children,
}) => (
  <Paper variant="outlined" sx={{ borderRadius: 3.5, borderColor: LINE, p: { xs: 2, md: 2.75 }, height: '100%', boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', color: INK }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {action && (
        <Button size="small" endIcon={<ArrowForward sx={{ fontSize: '16px !important' }} />} onClick={action.onClick}
          sx={{ color: ACCENT, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: ACCENT_SOFT } }}>
          {action.label}
        </Button>
      )}
    </Stack>
    {children}
  </Paper>
);

const Pill: React.FC<{ tone: Tone; pulse?: boolean; children: React.ReactNode }> = ({ tone, pulse, children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, px: 1.1, py: '3px', borderRadius: 999, bgcolor: TONES[tone].bg, color: TONES[tone].fg, fontSize: 12.5, fontWeight: 650, whiteSpace: 'nowrap' }}>
    <PulseDot tone={tone} size={7} pulse={pulse} />
    {children}
  </Box>
);

const SHIFT_LABEL: Record<Shift, string> = { TM: 'Mañana', TT: 'Tarde', TN: 'Noche' };
const ORIGIN_LABEL: Record<MaterialOrigin, string> = { Virgen: 'Virgen', RecuperadaPropia: 'Recuperada propia', RecuperadaComprada: 'Recuperada comprada' };
const ORIGIN_COLOR: Record<MaterialOrigin, string> = { Virgen: ACCENT, RecuperadaPropia: '#2F6FDB', RecuperadaComprada: '#7A5AF8' };

function arNow() {
  const now = new Date();
  const hour = Number(now.toLocaleString('en-US', { timeZone: AR_TZ, hour: 'numeric', hourCycle: 'h23' }));
  const day = now.toLocaleDateString('es-AR', { timeZone: AR_TZ, weekday: 'long', day: 'numeric', month: 'long' });
  return { hour, day: day.charAt(0).toUpperCase() + day.slice(1) };
}

const weekdayShort = (iso: string) =>
  new Date(`${iso}T12:00:00-03:00`).toLocaleDateString('es-AR', { timeZone: AR_TZ, weekday: 'short' }).replace('.', '');

const FactoryTodayPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, tenant } = useSelector((s: RootState) => s.auth);
  const layout = useMemo(() => plantLayoutForTenant(tenant), [tenant]);
  const { data, loading, updatedAt, failed, reload } = usePlantData(60_000);
  const alerts = useMemo(() => plantAlerts(data, layout), [data, layout]);

  const { hour, day } = arNow();
  const hello = hour < 13 ? 'Buen día' : hour < 20 ? 'Buenas tardes' : 'Buenas noches';
  const first = (user?.name || '').split(/\s+/)[0];

  const k = data.kpisToday;
  const todayShifts = shiftsToday(data);
  const injZones = layout?.zones.filter(z => z.kind === 'machine' && z.id.startsWith('iny-')) ?? [];
  const running = injZones.filter(z => { const m = findMachine(z, data.machines); return m && shiftsToday(data, m.id).length > 0; }).length;
  const red = data.stock.products.filter(p => p.level === 'rojo').length;
  const yellow = data.stock.products.filter(p => p.level === 'amarillo').length;
  const mpKg = data.materials.filter(m => m.origin).reduce((a, m) => a + m.currentStock, 0);
  const virgin = data.materials.filter(m => m.origin === 'Virgen').reduce((a, m) => a + m.currentStock, 0);
  const toDeliver = data.orders.reduce((a, o) => a + Math.max(0, o.balance), 0);
  const scalePath = layout?.zones.find(z => z.kind === 'scale') ? `/factory/plant?sector=${layout.zones.find(z => z.kind === 'scale')!.id}` : '/factory/materials';

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2.5, md: 3.5 }, pb: 6, maxWidth: 1480, mx: 'auto' }}>
      {/* Encabezado */}
      <Reveal>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }} sx={{ mb: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: MUTED, fontSize: 15, fontWeight: 500 }}>{day}{first ? ` · ${hello}, ${first}` : ''}</Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 30, md: 40 }, fontWeight: 850, letterSpacing: '-0.04em', lineHeight: 1.05, color: INK, mt: 0.5 }}>
              Hoy en planta
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 0.75, borderRadius: 999, bgcolor: TONES.ok.bg, color: TONES.ok.fg }}>
              <PulseDot tone="ok" size={8} pulse={!loading} />
              <Typography sx={{ fontSize: 13, fontWeight: 650, whiteSpace: 'nowrap' }}>
                {loading ? 'Conectando…' : `En vivo · ${updatedAt?.toLocaleTimeString('es-AR', { timeZone: AR_TZ, hour: '2-digit', minute: '2-digit' }) ?? ''}`}
              </Typography>
            </Stack>
            <Button variant="outlined" onClick={() => reload()} sx={{ minWidth: 0, px: 1.25, borderRadius: 2, borderColor: LINE, color: INK }} aria-label="Actualizar"><Refresh /></Button>
            <Button variant="contained" startIcon={<MapOutlined />} onClick={() => navigate('/factory/plant')} sx={{ ...accentButton, px: 2.25, py: 1 }}>Ver plano</Button>
          </Stack>
        </Stack>
      </Reveal>

      {failed > 0 && !loading && (
        <Typography sx={{ mb: 2, px: 2, py: 1.25, borderRadius: 2, bgcolor: TONES.warn.bg, color: TONES.warn.fg, fontSize: 14 }}>
          Hay {failed} dato{failed > 1 ? 's' : ''} que no se pudo traer; se reintenta solo.
        </Typography>
      )}

      {/* Avisos */}
      <Reveal i={1}>
        <AlertsStrip alerts={alerts} loading={loading} onGo={p => navigate(p)} />
      </Reveal>

      {/* Indicadores */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(3, minmax(0,1fr))', xl: 'repeat(6, minmax(0,1fr))' }, gap: 1.5, mb: 3 }}>
        <Kpi i={2} hero label="Producido hoy" value={<AnimatedNumber value={k?.goodQuantity ?? 0} />} unit="u." hint={k ? `${fmtQty(k.scrapQuantity)} rotos (${fmtPct(k.scrapPct, 1)})` : 'sin partes todavía'} />
        <Kpi i={3} label="Eficiencia" value={k?.efficiencyPct != null ? <AnimatedNumber value={k.efficiencyPct * 100} /> : '—'} unit={k?.efficiencyPct != null ? '%' : undefined}
          hint="contra el estándar" tone={k?.efficiencyPct != null ? (k.efficiencyPct >= 0.85 ? 'ok' : k.efficiencyPct >= 0.6 ? 'warn' : 'bad') : undefined} />
        <Kpi i={4} label="Inyectoras con parte" value={<><AnimatedNumber value={running} /><Box component="span" sx={{ color: MUTED, fontWeight: 650, fontSize: '0.6em' }}> / {injZones.length || data.machines.filter(m => !isMold(m)).length}</Box></>} hint={`${todayShifts.length} parte${todayShifts.length === 1 ? '' : 's'} cargado${todayShifts.length === 1 ? '' : 's'} hoy`} />
        <Kpi i={5} label="Productos bajos" value={<AnimatedNumber value={red + yellow} />} hint={red ? `${red} sin stock` : yellow ? 'cerca del mínimo' : 'todo en verde'} tone={red ? 'bad' : yellow ? 'warn' : 'ok'} />
        <Kpi i={6} label="Materia prima" value={<AnimatedNumber value={mpKg} />} unit="kg" hint={`virgen ${fmtQty(virgin)} kg`} />
        <Kpi i={7} label="Por entregar" value={<AnimatedNumber value={toDeliver} />} unit="u." hint={`${data.orders.length} OC abierta${data.orders.length === 1 ? '' : 's'}`} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.55fr) minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Reveal i={8}>
            <Card title="Inyectoras" subtitle="Qué está haciendo cada máquina hoy" action={{ label: 'Partes', onClick: () => navigate('/factory/production') }}>
              <MachinesGrid data={data} zones={layout?.zones.filter(z => z.kind === 'machine') ?? []} onGo={p => navigate(p)} />
            </Card>
          </Reveal>
          <Reveal i={9}>
            <ProductionCard data={data} />
          </Reveal>
        </Stack>

        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Reveal i={10}><StockCard items={data.stock.products} onGo={() => navigate('/factory/stock')} /></Reveal>
          <Reveal i={11}><MaterialsCard data={data} onGo={() => navigate('/factory/materials')} /></Reveal>
          <Reveal i={12}><MaintenanceCard data={data} onGo={() => navigate('/factory/maintenance')} /></Reveal>
          <Reveal i={13}><WeighingsCard data={data} onGo={() => navigate(scalePath)} /></Reveal>
        </Stack>
      </Box>
    </Box>
  );
};

// ==================== Bloques ====================

const Kpi: React.FC<{ i: number; label: string; value: React.ReactNode; unit?: string; hint?: React.ReactNode; tone?: Tone; hero?: boolean }> = ({
  i, label, value, unit, hint, tone, hero,
}) => {
  const fg = hero ? ACCENT : tone && tone !== 'neutral' ? TONES[tone].fg : INK;
  return (
    <Reveal i={i} style={{ minWidth: 0 }}>
      <Box sx={{
        height: '100%', borderRadius: 3, px: 2.25, py: 2, bgcolor: hero ? ACCENT_SOFT : '#fff',
        border: `1px solid ${hero ? '#FBD9C9' : LINE}`, boxShadow: hero ? 'none' : '0 1px 2px rgba(16,24,40,.04)',
      }}>
        <Typography sx={{ fontSize: 13, color: hero ? '#9A3A12' : MUTED, fontWeight: 550 }}>{label}</Typography>
        <Typography sx={{ mt: 0.5, fontSize: { xs: 26, md: 32 }, fontWeight: 850, letterSpacing: '-0.035em', lineHeight: 1.1, color: fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {value}{unit && <Box component="span" sx={{ fontSize: '0.5em', fontWeight: 700, ml: 0.6 }}>{unit}</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.5 }} noWrap>{hint}</Typography>}
      </Box>
    </Reveal>
  );
};

const AlertsStrip: React.FC<{ alerts: PlantAlert[]; loading: boolean; onGo: (p: string) => void }> = ({ alerts, loading, onGo }) => {
  if (loading && !alerts.length) return <Box sx={{ height: 56, mb: 3 }} />;
  if (!alerts.length) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3, px: 2, py: 1.5, borderRadius: 3, bgcolor: TONES.ok.bg }}>
        <CheckCircleRounded sx={{ color: TONES.ok.dot, fontSize: 28 }} />
        <Box>
          <Typography sx={{ fontWeight: 750, color: TONES.ok.fg }}>Todo en orden</Typography>
          <Typography sx={{ fontSize: 13.5, color: MUTED }}>Sin faltantes, mantenimientos vencidos ni recepciones pendientes.</Typography>
        </Box>
      </Stack>
    );
  }
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 750, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, mb: 1 }}>Para mirar hoy</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', xl: 'repeat(4, minmax(0,1fr))' }, gap: 1.25 }}>
        {alerts.slice(0, 8).map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, delay: 0.1 + i * 0.05, ease: EASE }} style={{ minWidth: 0 }}>
            <ButtonBase onClick={() => onGo(a.path)} sx={{
              width: '100%', height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.25, textAlign: 'left',
              px: 1.75, py: 1.4, borderRadius: 2.5, bgcolor: TONES[a.tone].bg, border: '1px solid transparent', transition: 'border-color .2s, transform .2s',
              '&:hover': { borderColor: TONES[a.tone].dot, transform: 'translateY(-1px)' },
            }}>
              <Box sx={{ pt: 0.6 }}><PulseDot tone={a.tone} size={9} pulse={a.tone === 'bad'} /></Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: TONES[a.tone].fg, lineHeight: 1.3 }}>{a.title}</Typography>
                {a.detail && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }} noWrap>{a.detail}</Typography>}
              </Box>
              <ArrowForward sx={{ fontSize: 16, color: TONES[a.tone].fg, mt: 0.4, opacity: 0.6 }} />
            </ButtonBase>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

interface Tile { key: string; label: string; n?: string; zone?: PlantZone; machine?: Machine }

const MachinesGrid: React.FC<{ data: PlantData; zones: PlantZone[]; onGo: (p: string) => void }> = ({ data, zones, onGo }) => {
  const tiles: Tile[] = zones.length
    ? zones.map(z => ({ key: z.id, label: z.label, n: z.id.startsWith('iny-') ? z.id.slice(4) : undefined, zone: z, machine: findMachine(z, data.machines) }))
    : data.machines.filter(m => !isMold(m)).map(m => ({ key: String(m.id), label: m.name, machine: m }));
  const mountedMolds = molds(data);

  if (!tiles.length) {
    return <Typography sx={{ color: MUTED, py: 3, textAlign: 'center' }}>Cargá las máquinas en Producción para verlas acá.</Typography>;
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', xl: 'repeat(3, minmax(0,1fr))' }, gap: 1.5 }}>
      {tiles.map((t, i) => {
        const m = t.machine;
        const st: ZoneStatus = t.zone ? zoneStatus(t.zone, data) : { tone: 'neutral', label: m ? 'Sin parte hoy' : 'Sin cargar' };
        const shifts = m ? shiftsToday(data, m.id) : [];
        const good = shifts.reduce((a, s) => a + s.goodQuantity, 0);
        const last = shifts[shifts.length - 1];
        const mold = m ? mountedMolds.find(x => x.mountedOnId === m.id) : undefined;
        const eff = m ? data.kpisToday?.machines.find(x => x.machineId === m.id)?.efficiencyPct : null;
        const effTone: Tone = eff == null ? 'neutral' : eff >= 0.85 ? 'ok' : eff >= 0.6 ? 'warn' : 'bad';
        const path = t.zone ? `/factory/plant?sector=${t.zone.id}` : '/factory/production';
        return (
          <motion.div key={t.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 + i * 0.05, ease: EASE }} style={{ minWidth: 0 }}>
            <ButtonBase onClick={() => onGo(path)} sx={{
              width: '100%', display: 'block', textAlign: 'left', borderRadius: 3, p: 1.75, bgcolor: '#fff', border: `1px solid ${LINE}`,
              position: 'relative', overflow: 'hidden', transition: 'box-shadow .2s, transform .2s, border-color .2s',
              '&:hover': { transform: 'translateY(-2px)', borderColor: '#E3DED7', boxShadow: '0 12px 24px -16px rgba(16,24,40,.35)' },
            }}>
              {st.active && (
                <motion.span
                  style={{ position: 'absolute', left: 0, top: 0, height: 3, background: TONES.ok.dot }}
                  initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 1.1, ease: EASE, delay: 0.3 + i * 0.05 }}
                />
              )}
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{
                  width: 42, height: 42, borderRadius: 2, border: `2px solid ${INK}`, display: 'grid', placeItems: 'center', flexShrink: 0,
                  fontWeight: 900, fontSize: t.n ? 22 : 13, color: '#1C2A5E', bgcolor: '#fff',
                }}>
                  {t.n ?? t.label.slice(0, 2).toUpperCase()}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 750, fontSize: 15, color: INK }} noWrap>{m?.name || t.label}</Typography>
                  <Pill tone={st.tone} pulse={st.active}>{st.label}</Pill>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 1.75 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 850, letterSpacing: '-0.035em', color: good ? INK : '#B4B8C0', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  <AnimatedNumber value={good} />
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>u. hoy</Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.5 }} noWrap>
                {last ? `${last.productName}${last.operatorName ? ` · ${last.operatorName}` : ''}` : mold ? 'Matriz montada, sin parte' : '—'}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: mold ? INK : '#B4B8C0', mt: 0.25 }} noWrap>
                {mold ? `Matriz ${mold.code ? `${mold.code} · ` : ''}${mold.name}` : 'Sin matriz montada'}
              </Typography>
              <Box sx={{ mt: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>Eficiencia</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: eff == null ? MUTED : TONES[effTone].fg }}>{eff == null ? 'sin estándar' : fmtPct(eff)}</Typography>
                </Stack>
                <ProgressBar value={(eff ?? 0) * 100} color={TONES[effTone].dot} height={6} />
              </Box>
            </ButtonBase>
          </motion.div>
        );
      })}
    </Box>
  );
};

const ProductionCard: React.FC<{ data: PlantData }> = ({ data }) => {
  const days = Array.from({ length: 7 }, (_, i) => addDaysIso(data.today, i - 6));
  const perDay = days.map(d => ({ d, v: data.shifts.filter(s => s.date.slice(0, 10) === d).reduce((a, s) => a + s.goodQuantity, 0) }));
  const max = Math.max(1, ...perDay.map(p => p.v));
  const week = perDay.reduce((a, p) => a + p.v, 0);
  const worked = perDay.filter(p => p.v > 0).length;
  const today = shiftsToday(data);
  const byShift = (['TM', 'TT', 'TN'] as Shift[]).map(s => ({ s, v: today.filter(x => x.shift === s).reduce((a, x) => a + x.goodQuantity, 0), n: today.filter(x => x.shift === s).length }));
  const products = [...(data.kpisToday?.products ?? [])].sort((a, b) => b.goodQuantity - a.goodQuantity).slice(0, 4);
  const topMax = Math.max(1, ...products.map(p => p.goodQuantity));

  return (
    <Card title="Producción" subtitle={`${fmtQty(week)} u. en los últimos 7 días${worked ? ` · promedio ${fmtQty(Math.round(week / worked))} por día trabajado` : ''}`}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.25fr) minmax(0, 1fr)' }, gap: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 170 }}>
            {perDay.map((p, i) => {
              const isToday = p.d === data.today;
              return (
                <Stack key={p.d} alignItems="center" sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                  <Typography sx={{ fontSize: 11, color: isToday ? ACCENT : MUTED, fontWeight: 700, mb: 0.5, fontVariantNumeric: 'tabular-nums', visibility: p.v ? 'visible' : 'hidden' }}>
                    {p.v >= 1000 ? `${(p.v / 1000).toFixed(p.v >= 10000 ? 0 : 1).replace('.', ',')}k` : p.v}
                  </Typography>
                  <Box sx={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <motion.div
                      title={`${fmtDayShort(p.d)}: ${fmtQty(p.v)} u.`}
                      style={{ width: '100%', borderRadius: 6, background: p.v ? (isToday ? ACCENT : '#F6B79C') : '#ECEAE6' }}
                      initial={{ height: 0 }} animate={{ height: `${Math.max(3, (p.v / max) * 100)}%` }}
                      transition={{ duration: 0.8, delay: 0.3 + i * 0.06, ease: EASE }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: isToday ? INK : MUTED, fontWeight: isToday ? 750 : 500, mt: 0.75, textTransform: 'capitalize' }}>
                    {isToday ? 'Hoy' : weekdayShort(p.d)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 750, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, mb: 1 }}>Hoy por turno</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1 }}>
            {byShift.map(s => (
              <Box key={s.s} sx={{ bgcolor: s.v ? ACCENT_SOFT : SURFACE, borderRadius: 2, px: 1.25, py: 1 }}>
                <Typography sx={{ fontSize: 12, color: s.v ? '#9A3A12' : MUTED }}>{SHIFT_LABEL[s.s]}</Typography>
                <Typography sx={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', color: s.v ? ACCENT : '#B4B8C0', fontVariantNumeric: 'tabular-nums' }}>
                  <AnimatedNumber value={s.v} />
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>{s.n} parte{s.n === 1 ? '' : 's'}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 750, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, mt: 2.25, mb: 1 }}>Lo más producido hoy</Typography>
          {products.length ? (
            <Stack spacing={1}>
              {products.map(p => (
                <Box key={p.productId}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontSize: 13.5, color: INK }} noWrap>{p.productName}</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(p.goodQuantity)}</Typography>
                  </Stack>
                  <Box sx={{ mt: 0.4 }}><ProgressBar value={(p.goodQuantity / topMax) * 100} height={5} /></Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 13.5, color: MUTED }}>Todavía no hay partes cargados hoy.</Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const LEVEL_TONE: Record<string, Tone> = { rojo: 'bad', amarillo: 'warn', verde: 'ok' };
const LEVEL_ORDER: Record<string, number> = { rojo: 0, amarillo: 1, verde: 2 };

const StockCard: React.FC<{ items: StockStatusItem[]; onGo: () => void }> = ({ items, onGo }) => {
  const counts = { rojo: 0, amarillo: 0, verde: 0, sin: 0 };
  items.forEach(i => { if (i.level === 'rojo' || i.level === 'amarillo' || i.level === 'verde') counts[i.level] += 1; else counts.sin += 1; });
  const total = Math.max(1, items.length);
  const worst = [...items]
    .filter(i => LEVEL_ORDER[i.level] != null)
    .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || (a.stock / Math.max(1, a.minStock)) - (b.stock / Math.max(1, b.minStock)))
    .slice(0, 5);
  const segs: { key: string; n: number; color: string; label: string }[] = [
    { key: 'rojo', n: counts.rojo, color: TONES.bad.dot, label: 'sin stock' },
    { key: 'amarillo', n: counts.amarillo, color: TONES.warn.dot, label: 'cerca del mínimo' },
    { key: 'verde', n: counts.verde, color: TONES.ok.dot, label: 'bien' },
    { key: 'sin', n: counts.sin, color: '#D6D3CE', label: 'sin mínimo' },
  ];
  return (
    <Card title="Stock de producto terminado" subtitle={`${items.length} producto${items.length === 1 ? '' : 's'} con semáforo`} action={{ label: 'Ver stock', onClick: onGo }}>
      <Box sx={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', bgcolor: SURFACE, gap: '2px' }}>
        {segs.filter(s => s.n).map((s, i) => (
          <motion.div key={s.key} style={{ background: s.color, height: '100%' }} initial={{ width: 0 }} animate={{ width: `${(s.n / total) * 100}%` }} transition={{ duration: 0.9, delay: 0.3 + i * 0.1, ease: EASE }} />
        ))}
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
        {segs.filter(s => s.n).map(s => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
            <Typography sx={{ fontSize: 13, color: MUTED }}><b style={{ color: INK }}>{s.n}</b> {s.label}</Typography>
          </Stack>
        ))}
      </Stack>
      {worst.length > 0 && (
        <Stack spacing={1.25} sx={{ mt: 2 }}>
          {worst.map(p => {
            const tone = LEVEL_TONE[p.level];
            const available = p.stock - (p.pendingOrders || 0);
            const target = Math.max(p.warningLevel || 0, p.minStock * 1.5, 1);
            return (
              <Box key={p.id}>
                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }} noWrap>{p.name}</Typography>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: TONES[tone].fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {fmtQty(available)} <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>/ mín {fmtQty(p.minStock)}</Box>
                  </Typography>
                </Stack>
                <Box sx={{ mt: 0.5 }}><ProgressBar value={(Math.max(0, available) / target) * 100} color={TONES[tone].dot} height={6} /></Box>
              </Box>
            );
          })}
        </Stack>
      )}
      {!items.length && <Typography sx={{ color: MUTED, fontSize: 14, mt: 1 }}>Configurá mínimos en Stock para ver el semáforo.</Typography>}
    </Card>
  );
};

const MaterialsCard: React.FC<{ data: PlantData; onGo: () => void }> = ({ data, onGo }) => {
  const origins: MaterialOrigin[] = ['Virgen', 'RecuperadaPropia', 'RecuperadaComprada'];
  const rows = origins.map(o => {
    const mats = data.materials.filter(m => m.origin === o);
    return { o, kg: mats.reduce((a, m) => a + m.currentStock, 0), n: mats.length, lots: mats.reduce((a, m) => a + m.openLots, 0) };
  });
  const max = Math.max(1, ...rows.map(r => r.kg));
  const out = data.grinding.filter(g => g.status === 'Enviado');
  const outKg = out.reduce((a, g) => a + g.sentKg, 0);
  return (
    <Card title="Materia prima" subtitle="Kilos en planta por origen" action={{ label: 'Ver MP', onClick: onGo }}>
      <Stack spacing={1.5}>
        {rows.map((r, i) => (
          <Box key={r.o}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>{ORIGIN_LABEL[r.o]}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                <AnimatedNumber value={r.kg} /> <Box component="span" sx={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>kg</Box>
              </Typography>
            </Stack>
            <Box sx={{ mt: 0.5, height: 10, borderRadius: 999, bgcolor: SURFACE, overflow: 'hidden' }}>
              <motion.div style={{ height: '100%', borderRadius: 999, background: ORIGIN_COLOR[r.o] }} initial={{ width: 0 }} animate={{ width: `${(r.kg / max) * 100}%` }} transition={{ duration: 0.9, delay: 0.3 + i * 0.08, ease: EASE }} />
            </Box>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>{r.n ? `${r.n} material${r.n === 1 ? '' : 'es'} · ${r.lots} lote${r.lots === 1 ? '' : 's'} abierto${r.lots === 1 ? '' : 's'}` : 'sin materiales con este origen'}</Typography>
          </Box>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2, px: 1.5, py: 1.1, borderRadius: 2, bgcolor: out.length ? TONES.info.bg : SURFACE }}>
        <PulseDot tone={out.length ? 'info' : 'neutral'} size={8} pulse={out.length > 0} />
        <Typography sx={{ fontSize: 13.5, color: out.length ? TONES.info.fg : MUTED, fontWeight: out.length ? 650 : 500 }}>
          {out.length ? `${fmtKg(outKg)} afuera en molienda (${out.length} viaje${out.length > 1 ? 's' : ''})` : 'Nada afuera en molienda'}
        </Typography>
      </Stack>
    </Card>
  );
};

const MaintenanceCard: React.FC<{ data: PlantData; onGo: () => void }> = ({ data, onGo }) => {
  const rows = data.overview
    .filter(o => o.overdueCount > 0 || o.soonCount > 0)
    .sort((a, b) => b.overdueCount - a.overdueCount || (a.nextDueDate || '').localeCompare(b.nextDueDate || ''))
    .slice(0, 5);
  const overdue = data.overview.reduce((a, o) => a + o.overdueCount, 0);
  return (
    <Card title="Mantenimiento" subtitle={overdue ? `${overdue} tarea${overdue > 1 ? 's' : ''} vencida${overdue > 1 ? 's' : ''}` : 'Máquinas y matrices'} action={{ label: 'Ver plan', onClick: onGo }}>
      {rows.length ? (
        <Stack divider={<Box sx={{ borderTop: `1px solid ${LINE}` }} />}>
          {rows.map(o => (
            <Stack key={o.machineId} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
              <PulseDot tone={o.overdueCount ? 'bad' : 'warn'} size={8} pulse={o.overdueCount > 0} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 650, color: INK }} noWrap>{o.name}</Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED }} noWrap>{o.nextDueTask || 'Tarea programada'}</Typography>
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: o.overdueCount ? TONES.bad.fg : TONES.warn.fg, whiteSpace: 'nowrap' }}>
                {o.overdueCount ? 'Vencido' : fmtDayShort(o.nextDueDate)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: TONES.ok.bg }}>
          <CheckCircleRounded sx={{ color: TONES.ok.dot }} />
          <Typography sx={{ fontSize: 14, color: TONES.ok.fg, fontWeight: 650 }}>Todo al día</Typography>
        </Stack>
      )}
    </Card>
  );
};

const WeighingsCard: React.FC<{ data: PlantData; onGo: () => void }> = ({ data, onGo }) => {
  const recent = [...data.receptions].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt)).slice(0, 5);
  return (
    <Card title="Últimos pesajes" subtitle="Ingresos de compra en la balanza" action={{ label: 'Abrir balanza', onClick: onGo }}>
      {recent.length ? (
        <Stack divider={<Box sx={{ borderTop: `1px solid ${LINE}` }} />}>
          {recent.map(r => (
            <Stack key={r.id} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: ACCENT_SOFT, color: ACCENT, display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 15, flexShrink: 0 }}>+</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 650, color: INK }} noWrap>{r.materialName}</Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED }} noWrap>{fmtWhen(r.receivedAt)}{r.supplierName ? ` · ${r.supplierName}` : ''}</Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.25}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtKg(r.kg)}</Typography>
                {!r.remitoNumber && <Pill tone="warn">sin factura</Pill>}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 14, color: MUTED }}>Sin pesajes en los últimos 45 días.</Typography>
      )}
    </Card>
  );
};

export default FactoryTodayPage;
